import { useEffect, useRef } from 'react';

/**
 * Горячие клавиши: 'mod+k' (⌘ на Mac, Ctrl в остальных), 'shift+n', 'esc',
 * последовательности через пробел: 'g d' — нажать G, затем D.
 */
export interface HotkeyBinding {
  readonly keys: string;
  readonly handler: (event: KeyboardEvent) => void;
  /** Срабатывать и при фокусе в поле ввода (по умолчанию — нет, кроме сочетаний с mod). */
  readonly allowInInputs?: boolean;
  /** Условие: пока оно ложно, клавиша не перехватывается и достаётся странице как обычно. */
  readonly when?: () => boolean;
}

export interface Chord {
  readonly key: string;
  readonly mod: boolean;
  readonly shift: boolean;
  readonly alt: boolean;
}

const ALIASES: Record<string, string> = { esc: 'escape', return: 'enter', up: 'arrowup', down: 'arrowdown' };

export function parseHotkey(keys: string): Chord[] {
  return keys
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((part) => {
      const tokens = part.split('+');
      const key = tokens.at(-1) ?? '';
      return {
        key: ALIASES[key] ?? key,
        mod: tokens.includes('mod'),
        shift: tokens.includes('shift'),
        alt: tokens.includes('alt'),
      };
    });
}

export const isMac = (): boolean => /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** Клавиша события без учёта раскладки: KeyG → «g», даже если включена русская раскладка. */
export function eventKey(event: Pick<KeyboardEvent, 'key' | 'code'>): string {
  if (/^Key[A-Z]$/.test(event.code)) return event.code.slice(3).toLowerCase();
  if (/^Digit\d$/.test(event.code)) return event.code.slice(5);
  return event.key.toLowerCase();
}

export function matchesChord(
  event: Pick<KeyboardEvent, 'key' | 'code' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>,
  chord: Chord,
  mac: boolean,
): boolean {
  const mod = mac ? event.metaKey : event.ctrlKey;
  return (
    eventKey(event) === chord.key &&
    mod === chord.mod &&
    event.shiftKey === chord.shift &&
    event.altKey === chord.alt
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
    target.role === 'combobox'
  );
}

const SEQUENCE_TIMEOUT_MS = 1200;

/** Подписка на горячие клавиши на время жизни компонента. */
export function useHotkeys(bindings: readonly HotkeyBinding[]): void {
  const ref = useRef(bindings);
  ref.current = bindings;

  useEffect(() => {
    const mac = isMac();
    let pending: { chord: Chord; at: number } | null = null;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) return;
      const typing = isTypingTarget(event.target);
      const now = performance.now();
      const previous = pending && now - pending.at < SEQUENCE_TIMEOUT_MS ? pending.chord : null;
      pending = null;

      for (const binding of ref.current) {
        const chords = parseHotkey(binding.keys);
        const last = chords.at(-1);
        if (!last) continue;
        const withMod = chords.some((c) => c.mod);
        if (typing && !binding.allowInInputs && !withMod) continue;
        if (binding.when && !binding.when()) continue;
        if (chords.length === 1 && matchesChord(event, last, mac)) {
          event.preventDefault();
          binding.handler(event);
          return;
        }
        if (chords.length === 2 && previous && matchesChord(event, last, mac)) {
          const first = chords[0];
          if (previous.key === first?.key && previous.mod === first.mod) {
            event.preventDefault();
            binding.handler(event);
            return;
          }
        }
      }
      if (!typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        pending = { chord: { key: eventKey(event), mod: false, shift: event.shiftKey, alt: false }, at: now };
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);
}

/** Подпись сочетания для интерфейса: 'mod+k' → ['⌘', 'K'] или ['Ctrl', 'K']. */
export function hotkeyLabel(keys: string, mac: boolean = isMac()): string[] {
  return parseHotkey(keys).flatMap((c) => [
    ...(c.mod ? [mac ? '⌘' : 'Ctrl'] : []),
    ...(c.shift ? ['⇧'] : []),
    ...(c.alt ? [mac ? '⌥' : 'Alt'] : []),
    c.key === 'escape' ? 'Esc' : c.key === 'enter' ? '↵' : c.key.toUpperCase(),
  ]);
}
